import type { Conversation } from '../../types/conversation';
import { isExternalWeComUserId } from '../../utils/wecomIds';

export type WeComConversationNameType = 'userName' | 'externalUserName' | 'chatName';
export type WeComExternalCorpDisplay = {
  label: string;
  className: string;
};

export const isWeComPlaceholderName = (name?: string | null) =>
  !name || name === 'undefined' || name === 'null' || /^\d+$/.test(name) || /^(wo|wm|wr)/.test(name);

export const getWeComGroupFallbackName = (conversation: Partial<Conversation>) => {
  if (conversation.group_type === 'internal') return '内部群';
  if (conversation.group_type === 'interconnect') return '互联群';
  if (conversation.group_type === 'external' || conversation.is_external_group) return '外部群';
  return '非本企业建群';
};

export const getWeComConversationFallbackName = (conversation: Partial<Conversation>) => {
  if (conversation.type === 'group') {
    return isWeComPlaceholderName(conversation.external_name || conversation.group_name)
      ? getWeComGroupFallbackName(conversation)
      : String(conversation.external_name || conversation.group_name || getWeComGroupFallbackName(conversation));
  }
  const name = conversation.external_name;
  if (!isWeComPlaceholderName(name)) return String(name || '');
  const id = String(conversation.external_userid || name || '');
  if (conversation.is_employee_chat || conversation.peer_type === 'employee') return '员工';
  if (conversation.peer_type === 'customer' || id.startsWith('wm')) return `外部客户 (${id.slice(-6)})`;
  return id || '未知';
};

export const getWeComConversationOpenDataId = (conversation?: Partial<Conversation> | null) => {
  if (!conversation) return '';
  if (conversation.type === 'group') {
    return String(
      conversation.wecom_room_id
      || conversation.room_id
      || conversation.chat_id
      || conversation.external_userid
      || '',
    );
  }
  if (conversation.is_employee_chat || conversation.peer_type === 'employee') {
    return String(conversation.peer_employee_open_userid || '');
  }
  return String(conversation.external_userid || '');
};

export const getWeComConversationNameType = (
  openid: string,
  conversation?: Partial<Conversation> | null,
): WeComConversationNameType => {
  if (conversation?.type === 'group') return 'chatName';
  if (conversation?.is_employee_chat || conversation?.peer_type === 'employee') return 'userName';
  if (conversation?.peer_type === 'customer') return 'externalUserName';
  if (openid.startsWith('wr')) return 'chatName';
  return isExternalWeComUserId(openid) ? 'externalUserName' : 'userName';
};

export const shouldUseWeComConversationNameOpenData = (
  conversation: Partial<Conversation> | null | undefined,
  nameType: WeComConversationNameType,
) => {
  if (!conversation) return false;
  if (nameType !== 'externalUserName') return true;
  return isWeComPlaceholderName(conversation.external_name);
};

/** 与 PC 会话存档统一的客户来源/企业名称展示规则。 */
export const getWeComExternalCorpDisplay = (
  conversation: Partial<Conversation> | null | undefined,
): WeComExternalCorpDisplay => {
  if (!conversation || conversation.type !== 'single' || conversation.is_employee_chat) {
    return { label: '', className: 'archive-external-corp' };
  }
  const externalUserId = String(conversation.external_userid || '').trim();
  const contactType = Number(conversation.external_contact_type || 0);

  // 企业微信的来源标签由客户账号类型决定。微信客户即使填写了公司资料，
  // 仍显示“@微信”，不能把备注公司名称误当成来源标签。
  if (contactType === 1 || externalUserId.startsWith('wm')) {
    return {
      label: '@微信',
      className: 'archive-external-corp archive-external-corp-wechat',
    };
  }

  const corpName = String(
    conversation.external_remark_corp_name
    || conversation.external_corp_name
    || conversation.external_corp_full_name
    || '',
  ).trim();
  if (contactType === 2 && corpName) {
    return {
      label: `@${corpName}`,
      className: 'archive-external-corp archive-external-corp-company',
    };
  }
  return { label: '', className: 'archive-external-corp' };
};

export const stripWeComExternalCorpSuffix = (name: string, suffix: string) => {
  const trimmedName = name.trim();
  const trimmedSuffix = suffix.trim();
  if (!trimmedName || !trimmedSuffix) return trimmedName;
  return trimmedName.endsWith(trimmedSuffix)
    ? trimmedName.slice(0, -trimmedSuffix.length).trim()
    : trimmedName;
};
